'use client'

import * as React from 'react'

import { cn } from '../lib/utils'

/* -------------------------------------------------------------- */
/* Pure helpers — exported so a host can render the SAME          */
/* fallback colour / initials outside of the component (chart     */
/* dot, table cell, etc.).                                        */
/* -------------------------------------------------------------- */

/**
 * Initials fallback used when there's no remote avatar (or it
 * fails to load). First character of each name part, uppercase.
 * Empty inputs collapse to "?".
 */
export function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const f = (firstName ?? '').trim().charAt(0)
  const l = (lastName ?? '').trim().charAt(0)
  const initials = `${f}${l}`.toUpperCase()
  return initials || '?'
}

/**
 * Tailwind colour palette for the deterministic fallback.
 * Order is STABLE — the index is computed off
 * `firstName.charCodeAt(0)`, so reshuffling this list would
 * re-colour every existing avatar in users' UIs. Add new colours
 * at the end if you ever extend.
 */
const FALLBACK_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-green-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-sky-500',
  'bg-blue-500',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-purple-500',
  'bg-fuchsia-500',
  'bg-pink-500',
  'bg-rose-500',
] as const

export function getAvatarFallbackColor(
  firstName: string | null | undefined
): string {
  const first = (firstName ?? '').trim()
  if (!first) return FALLBACK_COLORS[0] as string
  const idx = first.charCodeAt(0) % FALLBACK_COLORS.length
  return FALLBACK_COLORS[idx] as string
}

/* -------------------------------------------------------------- */
/* Component                                                      */
/* -------------------------------------------------------------- */

export interface UserAvatarProps {
  user: {
    first_name: string
    last_name: string
    /** Null for somebody added by name alone: there is no avatar to resolve,
     *  so the coloured initials stand in, which is what they were for. */
    email: string | null
  }
  /** Override resolution of the remote avatar URL. Receives the
   *  user's email and returns a URL. Pass `undefined` (or leave
   *  unset) to skip remote avatars and always render the
   *  coloured-initials fallback. */
  resolveAvatarUrl?: (email: string) => string | null
  size?: 'sm' | 'md' | 'lg'
  /** `circle` (default) matches every existing caller. `square` is a
   *  large-radius rounded square instead — for surfaces that want the
   *  avatar to read as a squircle rather than a coin. */
  shape?: 'circle' | 'square'
  /** Render a thin matching ring/border so stacked avatars sit
   *  cleanly on the page background. */
  bordered?: boolean
  className?: string
}

/**
 * Opinionated user-shaped avatar — pass a `{ first_name,
 * last_name, email }` object, get back the right thing.
 *
 * Renders a remote image when `resolveAvatarUrl` is supplied AND
 * the image loads successfully; otherwise falls back to
 * deterministic coloured-initials.
 *
 * Used across the booking flow (guest invites, pre-order
 * participants, future surfaces). For ad-hoc cases where you
 * want fine-grained control (badges, custom fallbacks, etc.) use
 * the compound `<Avatar>` primitive in `./avatar.tsx` instead.
 *
 * Design choice: NO hashing dep (`md5` etc.) is pulled into the
 * package for Gravatar URL building. Hosts that want Gravatar
 * supply their own resolver. Keeps the design package lean and
 * lets each app pick its own avatar service.
 */
export function UserAvatar({
  user,
  resolveAvatarUrl,
  size = 'md',
  shape = 'circle',
  bordered = false,
  className,
}: UserAvatarProps) {
  const [imgError, setImgError] = React.useState(false)
  const [imgLoaded, setImgLoaded] = React.useState(false)
  const imgRef = React.useRef<HTMLImageElement | null>(null)
  const remoteUrl = React.useMemo(
    () =>
      resolveAvatarUrl && user.email ? resolveAvatarUrl(user.email) : null,
    [resolveAvatarUrl, user.email]
  )

  const sizeClass =
    size === 'sm' ? 'size-7' : size === 'lg' ? 'size-10' : 'size-8'
  const initialsTextClass =
    size === 'sm' ? 'text-[10px]' : size === 'lg' ? 'text-sm' : 'text-xs'
  const shapeClass = shape === 'square' ? 'rounded-2xl' : 'rounded-full'
  const borderClass = bordered ? 'ring-2 ring-background' : ''

  const initials = getInitials(user.first_name, user.last_name)
  const bgColor = getAvatarFallbackColor(user.first_name)

  // Reset per URL: switching to a different user with a working avatar
  // must not stay on the broken-state fallback, and a new URL has not
  // loaded yet.
  React.useEffect(() => {
    setImgError(false)
    setImgLoaded(false)
  }, [remoteUrl])

  // A cached image can be complete before React attaches onLoad, in which
  // case the event never fires; read the element's own state once mounted.
  React.useEffect(() => {
    const el = imgRef.current
    if (el && el.complete && el.naturalWidth > 0) setImgLoaded(true)
  }, [remoteUrl])

  const showImage = Boolean(remoteUrl) && !imgError

  // The coloured initials are always the base layer, at the final size, so
  // there is nothing to shift and nothing to swap: a remote photo fades in
  // over them once it has actually loaded, and an image that 404s (Gravatar
  // with d=404, a stale URL) simply never appears. Before this the <img>
  // rendered alone as an empty box until the bytes arrived, then popped
  // in, or was torn down and replaced by the initials on error.
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden text-white',
        sizeClass,
        shapeClass,
        initialsTextClass,
        bgColor,
        borderClass,
        className
      )}
      aria-hidden={showImage ? undefined : true}
    >
      <span className="font-medium">{initials}</span>
      {showImage ? (
        <img
          ref={imgRef}
          src={remoteUrl as string}
          alt={`${user.first_name} ${user.last_name}`}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
          // Avatars are tiny and above the fold: fetch at once, decode off
          // the main thread. `lazy` deferred the request until after layout,
          // which is a large part of why the photo arrived late.
          loading="eager"
          decoding="async"
          referrerPolicy="no-referrer"
          className={cn(
            'absolute inset-0 size-full object-cover transition-opacity duration-200 ease-out',
            imgLoaded ? 'opacity-100' : 'opacity-0'
          )}
        />
      ) : null}
    </div>
  )
}
